/**
 * CRM data-access for the Owner Dashboard.
 *
 * Per-property data lives in either `pricelabs_*` or `guesty_*` tables,
 * decided by the `owners.source` column (one source per client_code).
 *
 * All queries push filters server-side and cast numerics with Number().
 */

import { getAuthenticatedCrmClient, getLocalAccessToken } from "@/lib/supabase-clients";
import { isMuExcluded } from "@/lib/utils";


export type CrmSource = "pricelabs" | "guesty";

export interface ChannelLink {
  url: string;
  label: string;
  platform: string;
}

export interface OwnerProperty {
  property_id: string;
  nickname: string | null;
  client_code: string;
  source: CrmSource;
  /** From CRM property.push_enabled. true = listed, false = unlisted, null = unknown. */
  push_enabled: boolean | null;
  channel_links: ChannelLink[];
  main_image: string | null;
  bedrooms: number | null;
  /** CRM property.liq_id — Listing IQ shared-listing id. */
  liq_id: string | null;
  /** CRM property.liq_created_at — created_at of the Listing IQ report behind liq_id. ISO string. */
  liq_created_at: string | null;
  /** CRM property.liq_last_analyzed — snapshot lastAnalyzed of the report behind liq_id (YYYY-MM-DD). The date shown to users. */
  liq_last_analyzed: string | null;
}

export interface LiqReport {
  id: string;
  /** ISO timestamp of the Sentinel shared_listing_iq_reports row (when the report was generated). */
  created_at: string;
  /** snapshot.lastAnalyzed as YYYY-MM-DD — the date shown to users. Null when it could not be read. */
  last_analyzed: string | null;
}

/** Date shown for a report: lastAnalyzed, falling back to the day it was generated (UTC). */
export function liqDisplayDate(r: Pick<LiqReport, "created_at" | "last_analyzed">): string {
  return r.last_analyzed ?? (r.created_at || "").slice(0, 10);
}

/**
 * Parse property.liq_reports and return it newest-first: by display date
 * (lastAnalyzed) desc, then generated-at desc. The head is the default report.
 */
export function parseLiqReports(raw: unknown): LiqReport[] {
  if (!Array.isArray(raw)) return [];
  const out: Array<LiqReport & { ts: number }> = [];
  for (const r of raw) {
    const rec = (r ?? {}) as Record<string, unknown>;
    const id = typeof rec.id === "string" ? rec.id : null;
    const created_at = typeof rec.created_at === "string" ? rec.created_at : null;
    if (!id || !created_at) continue;
    const la = typeof rec.last_analyzed === "string" && /^\d{4}-\d{2}-\d{2}/.test(rec.last_analyzed)
      ? rec.last_analyzed.slice(0, 10)
      : null;
    out.push({ id, created_at, last_analyzed: la, ts: new Date(created_at).getTime() || 0 });
  }
  out.sort((a, b) => {
    const da = liqDisplayDate(a);
    const db = liqDisplayDate(b);
    return (da < db ? 1 : da > db ? -1 : 0) || b.ts - a.ts || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
  });
  return out.map(({ id, created_at, last_analyzed }) => ({ id, created_at, last_analyzed }));
}

/**
 * Lazy fetch of all Listing IQ reports for the given properties (Links page
 * only — kept out of the shared property query to keep that payload small).
 * Goes through crm-read, which enforces the caller's ACL.
 */
export async function fetchLiqReports(propertyIds: string[]): Promise<Map<string, LiqReport[]>> {
  const out = new Map<string, LiqReport[]>();
  if (propertyIds.length === 0) return out;
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("Local session not ready");
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-read`;
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      Authorization: `Bearer ${localToken}`,
    },
    body: JSON.stringify({ action: "liq_reports", propertyIds }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`crm-read liq_reports failed: ${res.status} ${t}`);
  }
  const json = (await res.json()) as { rows?: Array<{ property_id?: string | null; liq_reports?: unknown }> };
  for (const r of json?.rows ?? []) {
    if (!r?.property_id) continue;
    const list = parseLiqReports(r.liq_reports);
    if (list.length) out.set(r.property_id, list);
  }
  return out;
}

function parseChannelLinks(raw: unknown): ChannelLink[] {
  if (!Array.isArray(raw)) return [];
  const out: ChannelLink[] = [];
  for (const l of raw) {
    if (!l || typeof l !== "object") continue;
    const url = typeof (l as any).url === "string" ? (l as any).url : null;
    const platform = typeof (l as any).platform === "string" ? (l as any).platform : null;
    if (!url || !platform) continue;
    out.push({
      url,
      platform,
      label: typeof (l as any).label === "string" ? (l as any).label : platform,
    });
  }
  return out;
}

export interface OverviewReservation {
  property_id: string;
  source: CrmSource;
  reservation_id: string;
  guest_name: string | null;
  channel: string | null;
  check_in: string;        // YYYY-MM-DD
  check_out: string;       // YYYY-MM-DD
  nights: number;
  guests: number | null;
  net: number;             // rental_revenue
  booking_status: string | null;
}

export interface MonthlyAggregate {
  property_id: string;
  source: CrmSource;
  month_start: string;             // YYYY-MM-01 (mapped from start_date)
  gross_rent: number;              // revenue
  net_rent: number;                // revenue (until owner-fee model wired)
  occupancy_rate: number;          // 0..1 (sum of channels for gpm / occupancy_rate for ppm)
  market_occupancy: number | null; // 0..1 (market_or)
  occupied_nights: number;         // daysInMonth × occupancy_rate_guest (gpm) or × occupancy_rate (ppm)
  // Guesty-only per-sub-channel breakdown (0..1 each). Undefined for pricelabs.
  occupancy_rate_guest?: number;
  occupancy_rate_portal?: number;
  occupancy_rate_direct?: number;
  occupancy_rate_ical?: number;
}

// ─── Owners ──────────────────────────────────────────────────────────────

/** Map every client_code → its source (pricelabs|guesty). */
export async function fetchOwnerSources(): Promise<Map<string, CrmSource>> {
  const client = getAuthenticatedCrmClient();
  if (!client) throw new Error("CRM session not ready");
  const { data, error } = await client
    .from("clients")
    .select("client_code, source")
    .limit(5000);
  if (error) throw error;
  const m = new Map<string, CrmSource>();
  for (const r of data ?? []) {
    if (r.client_code && (r.source === "pricelabs" || r.source === "guesty")) {
      m.set(r.client_code, r.source);
    }
  }
  return m;
}

// ─── Properties accessible to the user ──────────────────────────────────
//
// SINGLE source of truth for both the Client and Property pills across
// every page in the app. Reads the CRM `property` table only. `source`
// comes directly from `property.source` (no `owners`-table join, no ACL
// fallback). When `property.source` is null/unknown, falls back to
// "guesty" so the row is never silently dropped.

type AccessUniverse =
  | { mode: "by_client_code"; clientCodes: Set<string> | null }   // null = ALL
  | { mode: "by_property_id"; propertyIds: Set<string> | null }; // null = ALL

function toCrmSource(raw: unknown): CrmSource | null {
  return raw === "pricelabs" || raw === "guesty" ? raw : null;
}

// Warn once per (property, client_code) pair when we fall back to the
// client-level source because property.source is missing/unknown. Helps spot
// CRM data-quality issues without changing routing behaviour.
const _sourceFallbackWarned = new Set<string>();
function resolveEffectiveSource(
  propertyRaw: unknown,
  clientRaw: unknown,
  propertyId: string,
  clientCode: string,
): CrmSource {
  const p = toCrmSource(propertyRaw);
  if (p) return p;
  const c = toCrmSource(clientRaw);
  if (c) {
    const key = `${propertyId}|${clientCode}`;
    if (!_sourceFallbackWarned.has(key)) {
      _sourceFallbackWarned.add(key);
      // eslint-disable-next-line no-console
      console.warn(
        `[owner/crm] property ${propertyId} (client ${clientCode}) has no property.source; falling back to clients.source="${c}"`,
      );
    }
    return c;
  }
  return "guesty"; // last-resort default, matches historical behaviour
}

async function fetchPropertiesViaProxy(): Promise<any[]> {
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("Local session not ready");
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-read`;
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      Authorization: `Bearer ${localToken}`,
    },
    body: JSON.stringify({ action: "listings" }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`crm-read listings failed: ${res.status} ${t}`);
  }
  const json = await res.json();
  return (json?.rows as any[]) ?? [];
}

function mapPropertyRows(rows: any[], clientSourceMap?: Map<string, CrmSource>): OwnerProperty[] {
  const out: OwnerProperty[] = [];
  for (const r of rows) {
    if (!r?.property_id || !r?.client_code) continue;
    if (isMuExcluded(r.nickname)) continue;
    // client_source may arrive on the row (proxy path) or via the map
    // (direct CRM path); property.source always wins if known.
    const clientRaw = r.client_source ?? clientSourceMap?.get(r.client_code) ?? null;
    out.push({
      property_id: r.property_id,
      nickname: r.nickname ?? null,
      client_code: r.client_code,
      source: resolveEffectiveSource(r.source, clientRaw, r.property_id, r.client_code),
      push_enabled: r.push_enabled ?? null,
      channel_links: parseChannelLinks((r as any).channel_links),
      main_image: (r as any).main_image ?? null,
      bedrooms: (r as any).bedrooms != null ? Number((r as any).bedrooms) : null,
      liq_last_analyzed: typeof (r as any).liq_last_analyzed === "string" && (r as any).liq_last_analyzed ? (r as any).liq_last_analyzed.slice(0, 10) : null,
      liq_created_at: typeof (r as any).liq_created_at === "string" && (r as any).liq_created_at ? (r as any).liq_created_at : null,
      liq_id: typeof (r as any).liq_id === "string" && (r as any).liq_id.trim() ? (r as any).liq_id.trim() : null,
    });
  }
  return out;
}

async function fetchPropertiesFromCrm(universe: AccessUniverse): Promise<OwnerProperty[]> {
  const client = getAuthenticatedCrmClient();
  if (!client) throw new Error("CRM session not ready");

  let query = client
    .from("property")
    .select("property_id, client_code, nickname, push_enabled, source, channel_links, main_image, bedrooms, liq_id, liq_created_at, liq_last_analyzed")
    .order("nickname", { ascending: true })
    .limit(100000);

  if (universe.mode === "by_client_code") {
    if (universe.clientCodes !== null) {
      if (universe.clientCodes.size === 0) return [];
      query = query.in("client_code", Array.from(universe.clientCodes));
    }
  } else {
    if (universe.propertyIds !== null) {
      if (universe.propertyIds.size === 0) return [];
      query = query.in("property_id", Array.from(universe.propertyIds));
    }
  }

  const { data, error } = await query;
  if (error) throw error;

  // Batch-fetch clients.source for the distinct client_codes so we can
  // corroborate a missing/unknown property.source with the owner-level value.
  const propRows = (data ?? []) as any[];
  const clientCodes = Array.from(new Set(
    propRows.map((r) => r?.client_code).filter((c): c is string => !!c),
  ));
  const clientSourceMap = new Map<string, CrmSource>();
  if (clientCodes.length) {
    const { data: clientRows, error: clientErr } = await client
      .from("clients")
      .select("client_code, source")
      .in("client_code", clientCodes);
    if (!clientErr) {
      for (const r of (clientRows ?? []) as Array<{ client_code: string | null; source: unknown }>) {
        if (!r?.client_code) continue;
        const s = toCrmSource(r.source);
        if (s) clientSourceMap.set(r.client_code, s);
      }
    }
  }

  const mapped = mapPropertyRows(propRows, clientSourceMap);

  // Fallback: if CRM RLS filtered everything out (empty result despite a
  // non-empty restricted universe), retry via crm-read proxy which uses the
  // service role and enforces ACL from local user_owner_access. This covers
  // client-role users whose CRM session cannot see property rows directly.
  if (mapped.length === 0) {
    const restricted =
      (universe.mode === "by_client_code" && universe.clientCodes && universe.clientCodes.size > 0) ||
      (universe.mode === "by_property_id" && universe.propertyIds && universe.propertyIds.size > 0);
    if (restricted) {
      try {
        const rows = await fetchPropertiesViaProxy();
        return mapPropertyRows(rows);
      } catch {
        return mapped;
      }
    }
  }

  return mapped;
}

/** Client / admin / AM flow — universe = property.client_code IN (...). */
export async function fetchAccessibleProperties(
  allowedClientCodes: Set<string> | null,
  _defaultSource?: CrmSource, // kept for backward compat; ignored
): Promise<OwnerProperty[]> {
  return fetchPropertiesFromCrm({ mode: "by_client_code", clientCodes: allowedClientCodes });
}

/** Owner / owner_test flow — universe = property.property_id IN (...). */
export async function fetchAccessiblePropertiesByPropertyIds(
  allowedPropertyIds: Set<string> | null,
  _defaultSource?: CrmSource, // kept for backward compat; ignored
): Promise<OwnerProperty[]> {
  return fetchPropertiesFromCrm({ mode: "by_property_id", propertyIds: allowedPropertyIds });
}





// ─── Resilient select helper ────────────────────────────────────────────
//
// Some columns exist on guesty_* but not on pricelabs_* (and vice-versa).
// Rather than maintain a hand-curated list per table, we issue the query
// with the full wishlist and, if Postgres complains that a column does
// not exist (SQLSTATE 42703), drop that column and retry. Trimmed lists
// are cached per table for the session so we only pay the cost once.

const trimmedColsCache = new Map<string, string[]>();

function parseMissingColumn(err: any): string | null {
  const msg: string = err?.message ?? "";
  // Examples: 'column pricelabs_reservations.guest_name does not exist'
  //           'column "guest_name" does not exist'
  const m =
    msg.match(/column\s+(?:"?[\w]+"?\.)?"?([\w]+)"?\s+does not exist/i) ||
    msg.match(/column\s+"([^"]+)"\s+does not exist/i);
  return m ? m[1] : null;
}

async function selectWithFallback<T = any>(
  client: any,
  table: string,
  wishlist: string[],
  build: (q: any) => any,
  filterCols: string[] = [],   // columns referenced by .gte/.lte/.neq/.in — drop those calls if missing
): Promise<{ data: T[]; cols: Set<string> }> {
  let cols = trimmedColsCache.get(table) ?? [...wishlist];
  let droppedFilters = new Set<string>();

  // up to N attempts; each removes one offending column
  for (let attempt = 0; attempt < wishlist.length + filterCols.length; attempt++) {
    const q = client.from(table).select(cols.join(", "));
    const filtered = build({
      query: q,
      hasCol: (c: string) => cols.includes(c) && !droppedFilters.has(c),
    });
    const { data, error } = await filtered;
    if (!error) {
      trimmedColsCache.set(table, cols);
      return { data: (data ?? []) as T[], cols: new Set(cols) };
    }
    const missing = parseMissingColumn(error);
    if (!missing) throw error;
    if (cols.includes(missing)) {
      cols = cols.filter((c) => c !== missing);
    } else if (filterCols.includes(missing)) {
      droppedFilters.add(missing);
    } else {
      throw error;
    }
  }
  throw new Error(`Could not resolve columns for ${table}`);
}

// ─── Reservations (split by source, merged) ─────────────────────────────

function splitBySource(
  propertyIds: string[],
  sourceMap: Map<string, CrmSource>
): { pricelabs: string[]; guesty: string[] } {
  const out = { pricelabs: [] as string[], guesty: [] as string[] };
  for (const id of propertyIds) {
    const s = sourceMap.get(id);
    if (s === "pricelabs") out.pricelabs.push(id);
    else if (s === "guesty") out.guesty.push(id);
  }
  return out;
}

const RESV_WISHLIST_PRICELABS = [
  "property_id", "reservation_id",
  "check_in", "check_out", "no_of_days",
  "rental_revenue", "booking_status", "pms", "currency",
];
// On guesty_reservations the booking channel lives in `source`,
// nights in `nights_count`, status in `status`, revenue in `netincome_usd`.
const RESV_WISHLIST_GUESTY = [
  "property_id", "reservation_id", "guest_name", "source", "status",
  "check_in", "check_out", "nights_count", "guests_count",
  "netincome_usd", "net_income", "commission_usd", "currency",
];

export async function fetchReservationsRange(
  propertyIds: string[],
  sourceMap: Map<string, CrmSource>,
  fromDateISO: string,
  toDateISO: string,
  dateColumn: "check_in" | "booked_date" = "check_in",
  options: { confirmedOnly?: boolean; dateRangeMode?: "single" | "checkin_or_checkout" } = {},
): Promise<OverviewReservation[]> {
  const { confirmedOnly = false, dateRangeMode = "single" } = options;
  if (!propertyIds.length) return [];

  // Route through the `crm-perf-reservations` edge function instead of
  // querying CRM directly with the caller's CRM JWT. CRM-side RLS on
  // `guesty_reservations` filters per CRM user, which caused two local users
  // with identical local ACL to see DIFFERENT Reservations counts on the
  // Performance KPI grid. The proxy uses the CRM service role and enforces
  // our local ACL, so every local user with the same scope sees the same data.
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("Local session not ready");

  const { pricelabs, guesty } = splitBySource(propertyIds, sourceMap);

  const callProxy = async (src: CrmSource, ids: string[]): Promise<any[]> => {
    if (!ids.length) return [];
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-perf-reservations`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localToken}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
      },
      body: JSON.stringify({
        source: src,
        propertyIds: ids,
        fromDate: fromDateISO,
        toDate: toDateISO,
        dateColumn,
        confirmedOnly,
        dateRangeMode,
      }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`crm-perf-reservations failed: ${res.status} ${body}`);
    }
    const json = await res.json();
    return Array.isArray(json?.rows) ? json.rows : [];
  };

  const shape = (rows: any[], src: CrmSource): OverviewReservation[] =>
    rows.map((r) => ({
      property_id: r.property_id,
      source: src,
      reservation_id: r.reservation_id ?? "",
      guest_name: r.guest_name ?? null,
      channel: src === "guesty" ? (r.source_group ?? r.source ?? null) : (r.pms ?? null),
      check_in: r.check_in ?? "",
      check_out: r.check_out ?? "",
      nights: Number(r.no_of_days ?? r.nights_count ?? 0),
      guests: r.guests_count != null ? Number(r.guests_count) : null,
      net: Number(r.rental_revenue ?? r.netincome_usd ?? r.net_income ?? 0),
      booking_status: r.booking_status ?? r.status ?? null,
    } satisfies OverviewReservation));

  const [a, b] = await Promise.all([
    callProxy("pricelabs", pricelabs).then((rows) => shape(rows, "pricelabs")),
    callProxy("guesty", guesty).then((rows) => shape(rows, "guesty")),
  ]);
  return [...a, ...b];
}


// ─── Monthly aggregates (ppm / gpm) ─────────────────────────────────────

const GPM_WISHLIST = [
  "property_id", "start_date", "revenue", "market_or",
  "occupancy_rate_guest", "occupancy_rate_portal",
  "occupancy_rate_direct", "occupancy_rate_ical",
];
const PPM_WISHLIST = [
  "property_id", "start_date", "revenue", "occupancy_rate", "market_or",
];

function shapeMonthlyRow(src: CrmSource, r: any): MonthlyAggregate {
  const rate = src === "guesty"
    ? Number(r.occupancy_rate_guest ?? 0)
      + Number(r.occupancy_rate_portal ?? 0)
      + Number(r.occupancy_rate_direct ?? 0)
      + Number(r.occupancy_rate_ical ?? 0)
    : Number(r.occupancy_rate ?? 0);
  const rateForNights = src === "guesty"
    ? Number(r.occupancy_rate_guest ?? 0)
    : Number(r.occupancy_rate ?? 0);
  const revenue = Number(r.revenue ?? 0);
  const startDate: string = r.start_date ?? "";
  let daysInMonth = 0;
  if (/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
    const [yy, mm] = startDate.split("-").map(Number);
    daysInMonth = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  }
  return {
    property_id: r.property_id,
    source: src,
    month_start: startDate,
    gross_rent: revenue,
    net_rent: revenue,
    occupancy_rate: Math.min(1, rate),
    market_occupancy: r.market_or != null ? Number(r.market_or) : null,
    occupied_nights: daysInMonth * Math.min(1, rateForNights),
    ...(src === "guesty"
      ? {
          occupancy_rate_guest: Number(r.occupancy_rate_guest ?? 0),
          occupancy_rate_portal: Number(r.occupancy_rate_portal ?? 0),
          occupancy_rate_direct: Number(r.occupancy_rate_direct ?? 0),
          occupancy_rate_ical: Number(r.occupancy_rate_ical ?? 0),
        }
      : {}),
  } satisfies MonthlyAggregate;
}

async function callCrmReadProxy(payload: Record<string, unknown>): Promise<any[]> {
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("Local session not ready");
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-read`;
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      Authorization: `Bearer ${localToken}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`crm-read ${payload.action} failed: ${res.status} ${t}`);
  }
  const json = await res.json();
  return (json?.rows as any[]) ?? [];
}

async function fetchMonthlyAggregatesViaProxy(
  propertyIds: string[],
  sourceMap: Map<string, CrmSource>,
  fromMonthISO: string,
  toMonthISO: string,
): Promise<MonthlyAggregate[]> {
  const { pricelabs, guesty } = splitBySource(propertyIds, sourceMap);
  const [pl, gs] = await Promise.all([
    pricelabs.length
      ? callCrmReadProxy({ action: "monthly_aggregates", source: "pricelabs", propertyIds: pricelabs, fromMonthISO, toMonthISO })
      : Promise.resolve([]),
    guesty.length
      ? callCrmReadProxy({ action: "monthly_aggregates", source: "guesty", propertyIds: guesty, fromMonthISO, toMonthISO })
      : Promise.resolve([]),
  ]);
  return [
    ...pl.map((r) => shapeMonthlyRow("pricelabs", r)),
    ...gs.map((r) => shapeMonthlyRow("guesty", r)),
  ];
}

export async function fetchMonthlyAggregates(
  propertyIds: string[],
  sourceMap: Map<string, CrmSource>,
  fromMonthISO: string,
  toMonthISO: string,
): Promise<MonthlyAggregate[]> {
  if (!propertyIds.length) return [];
  const client = getAuthenticatedCrmClient();
  if (!client) throw new Error("CRM session not ready");

  const { pricelabs, guesty } = splitBySource(propertyIds, sourceMap);

  const ID_CHUNK = 150;
  const runFor = async (table: string, ids: string[], src: CrmSource) => {
    if (!ids.length) return [] as MonthlyAggregate[];
    const wishlist = src === "guesty" ? GPM_WISHLIST : PPM_WISHLIST;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += ID_CHUNK) chunks.push(ids.slice(i, i + ID_CHUNK));
    const perChunk = await Promise.all(chunks.map(async (chunkIds) => {
      const { data } = await selectWithFallback(
        client,
        table,
        wishlist,
        ({ query, hasCol }: any) => {
          let q = query.in("property_id", chunkIds).limit(50000);
          if (hasCol("start_date")) q = q.gte("start_date", fromMonthISO).lte("start_date", toMonthISO);
          return q;
        },
        ["start_date"],
      );
      return data;
    }));
    return perChunk.flat().map((r: any) => shapeMonthlyRow(src, r));
  };


  const [a, b] = await Promise.all([
    runFor("ppm", pricelabs, "pricelabs"),
    runFor("gpm", guesty, "guesty"),
  ]);
  const merged = [...a, ...b];

  // Fallback: CRM RLS may filter out everything for client-role users.
  // Retry via crm-read proxy (service role + local ACL enforcement).
  if (merged.length === 0) {
    try {
      return await fetchMonthlyAggregatesViaProxy(propertyIds, sourceMap, fromMonthISO, toMonthISO);
    } catch {
      return merged;
    }
  }
  return merged;
}


// ─── All-time data range (min/max start_date across ppm + gpm) ──────────
export async function fetchAllTimeMonthRange(
  propertyIds: string[],
  sourceMap: Map<string, CrmSource>,
): Promise<{ fromMonth: string; toMonth: string } | null> {
  if (!propertyIds.length) return null;
  const client = getAuthenticatedCrmClient();
  if (!client) throw new Error("CRM session not ready");

  const { pricelabs, guesty } = splitBySource(propertyIds, sourceMap);
  const ID_CHUNK = 150;

  const runEndpoint = async (table: string, ids: string[]) => {
    if (!ids.length) return [] as string[];
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += ID_CHUNK) chunks.push(ids.slice(i, i + ID_CHUNK));
    const results = await Promise.all(chunks.flatMap((chunkIds) => [
      client.from(table)
        .select("start_date")
        .in("property_id", chunkIds)
        .gt("revenue", 0)
        .order("start_date", { ascending: true })
        .limit(1),
      client.from(table)
        .select("start_date")
        .in("property_id", chunkIds)
        .gt("revenue", 0)
        .order("start_date", { ascending: false })
        .limit(1),
    ]));
    const dates: string[] = [];
    for (const r of results) {
      const rows = (r?.data ?? []) as Array<{ start_date?: string | null }>;
      for (const row of rows) {
        if (row?.start_date && /^\d{4}-\d{2}-\d{2}$/.test(row.start_date)) {
          dates.push(row.start_date);
        }
      }
    }
    return dates;
  };

  const [ppmDates, gpmDates] = await Promise.all([
    runEndpoint("ppm", pricelabs),
    runEndpoint("gpm", guesty),
  ]);
  let all = [...ppmDates, ...gpmDates];

  // Fallback via crm-read proxy when CRM RLS returns nothing.
  if (all.length === 0) {
    try {
      const [pl, gs] = await Promise.all([
        pricelabs.length
          ? callCrmReadProxy({ action: "all_time_month_range", source: "pricelabs", propertyIds: pricelabs })
          : Promise.resolve([]),
        guesty.length
          ? callCrmReadProxy({ action: "all_time_month_range", source: "guesty", propertyIds: guesty })
          : Promise.resolve([]),
      ]);
      const dates: string[] = [];
      for (const r of [...pl, ...gs]) {
        const d = (r as any)?.start_date;
        if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) dates.push(d);
      }
      all = dates;
    } catch {
      // fall through
    }
  }

  if (!all.length) return null;
  let min = all[0];
  let max = all[0];
  for (const d of all) {
    if (d < min) min = d;
    if (d > max) max = d;
  }
  return { fromMonth: min.slice(0, 7), toMonth: max.slice(0, 7) };
}


